import { useState } from 'react';
import { useNavigate } from 'react-router';
import { SESSION_KINDS, newOpsId, type OpsContainer, type OpsTrip } from '@ecobox/firebase';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Select,
  TimeInState,
  useNow,
  useToast,
} from '@ecobox/ui';
import { SessionCards } from '../components/SessionCards';
import { useBranches, useExportContainers, useLoadingTrips, useVehicles } from '../hooks/useOps';
import {
  containerLabel,
  containerStatusLabel,
  containerStatusTone,
  dueClock,
  tripLabel,
} from '../labels';
import { useBranch, useServices, useSession } from '../services';
import { useAllSessions, usePermissions, useSessionEngine } from '../sessions';

type Mode = 'trip' | 'container';

export function LoadPage() {
  const branch = useBranch().current;
  const { uid } = useSession();
  const engine = useSessionEngine();
  const sessions = useAllSessions();
  const perms = usePermissions();
  const navigate = useNavigate();
  const canTrip = perms.has('trip.load');
  const canContainer = perms.has('container.load');
  const [mode, setMode] = useState<Mode>(canTrip || !canContainer ? 'trip' : 'container');

  const mine = sessions.filter(
    (s) =>
      s.uid === uid &&
      s.finishedAt === undefined &&
      s.branchId === branch &&
      SESSION_KINDS[s.kind].direction === 'load',
  );

  const resume = (kind: 'load_trip' | 'load_container', targetId: string, targetLabel: string) => {
    if (!uid || !branch) return;
    const session = engine.start({ kind, targetId, targetLabel, uid, branchId: branch });
    void navigate(`/session/${session.id}`);
  };

  return (
    <div className="ops-stack">
      <h1 className="ops-title">Load</h1>
      <SessionCards sessions={mine} />
      {canTrip && canContainer ? (
        <div
          className="ops-two"
          role="group"
          aria-label="What are you loading?"
          data-tour="load-mode"
        >
          <Button
            size="touch"
            variant={mode === 'trip' ? 'primary' : 'secondary'}
            aria-pressed={mode === 'trip'}
            onClick={() => setMode('trip')}
          >
            Trip
          </Button>
          <Button
            size="touch"
            variant={mode === 'container' ? 'primary' : 'secondary'}
            aria-pressed={mode === 'container'}
            onClick={() => setMode('container')}
          >
            Container
          </Button>
        </div>
      ) : null}
      {mode === 'trip' && canTrip ? (
        <TripPicker branch={branch} onPick={(t) => resume('load_trip', t.id, tripLabel(t))} />
      ) : null}
      {mode === 'container' && canContainer ? (
        <ContainerPicker
          branch={branch}
          onPick={(c) => resume('load_container', c.id, containerLabel(c))}
        />
      ) : null}
      {!canTrip && !canContainer ? (
        <EmptyState title="Loading is not part of your role" description="Ask your manager." />
      ) : null}
    </div>
  );
}

function TripPicker({ branch, onPick }: { branch: string | null; onPick: (t: OpsTrip) => void }) {
  const trips = useLoadingTrips(branch);
  const branches = useBranches();
  const vehicles = useVehicles(branch);
  const { writer } = useServices();
  const engine = useSessionEngine();
  const { uid } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [to, setTo] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [driver, setDriver] = useState('');
  const [busy, setBusy] = useState(false);
  const now = useNow();

  const open = () => {
    if (!branch || !uid || !to) return;
    setBusy(true);
    try {
      const tripId = newOpsId('trp');
      writer.send('openTrip', {
        tripId,
        toBranchId: to,
        ...(vehicle ? { vehicleId: vehicle } : {}),
        driverName: driver.trim(),
      });
      const plate = vehicles.data.find((v) => v.id === vehicle)?.plate;
      const label = tripLabel({
        fromBranchId: branch,
        toBranchId: to,
        ...(plate ? { vehiclePlate: plate } : {}),
      });
      const session = engine.start({
        kind: 'load_trip',
        targetId: tripId,
        targetLabel: label,
        uid,
        branchId: branch,
      });
      void navigate(`/session/${session.id}`);
    } catch (err) {
      setBusy(false);
      toast.show({
        title: 'Trip not opened',
        message: err instanceof Error ? err.message : String(err),
        tone: 'danger',
      });
    }
  };

  return (
    <>
      <Card title="Open a new trip" data-tour="new-trip">
        <div className="ops-stack">
          <Field label="Going to">
            {(p) => (
              <Select {...p} size="touch" value={to} onChange={(e) => setTo(e.target.value)}>
                <option value="">Choose a branch</option>
                {branches.data
                  .filter((b) => b.code !== branch)
                  .map((b) => (
                    <option key={b.code} value={b.code}>
                      {b.code} · {b.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          <Field label="Vehicle (optional)">
            {(p) => (
              <Select
                {...p}
                size="touch"
                value={vehicle}
                onChange={(e) => setVehicle(e.target.value)}
              >
                <option value="">No vehicle chosen</option>
                {vehicles.data.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.plate} · {v.type}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Driver (optional)">
            {(p) => (
              <Input
                {...p}
                size="touch"
                value={driver}
                maxLength={80}
                onChange={(e) => setDriver(e.target.value)}
              />
            )}
          </Field>
          <Button
            size="touch"
            variant="cta"
            block
            disabled={!to || !branch}
            loading={busy}
            onClick={open}
          >
            Open trip and start scanning
          </Button>
        </div>
      </Card>
      <h2 className="ops-subtitle" data-tour="open-trips">
        Trips being loaded at {branch ?? '…'}
      </h2>
      {trips.error ? (
        <p role="alert" className="ops-error">
          {trips.error}
        </p>
      ) : null}
      {trips.data.length === 0 ? (
        <p className="ops-muted">
          {trips.loading ? 'Loading…' : 'No open trips here. Open one above.'}
        </p>
      ) : (
        <ul className="ops-list" aria-label="Open trips">
          {trips.data.map((t) => (
            <li key={t.id} className="ops-row">
              <div className="ops-row__top">
                <strong>{tripLabel(t)}</strong>
                <Badge tone="info">{t.counts.loaded} loaded</Badge>
              </div>
              <div className="ops-muted">
                {t.driverName ? `${t.driverName} · ` : ''}
                <TimeInState
                  since={t.stateSince}
                  clock={dueClock(t.nextDueAt, now)}
                  suffix="loading"
                />
              </div>
              <Button size="touch" variant="primary" block onClick={() => onPick(t)}>
                Scan onto this trip
              </Button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function ContainerPicker({
  branch,
  onPick,
}: {
  branch: string | null;
  onPick: (c: OpsContainer) => void;
}) {
  const containers = useExportContainers(branch);
  const now = useNow();
  const loadable = containers.data.filter((c) => c.status === 'planned' || c.status === 'loading');
  return (
    <>
      <h2 className="ops-subtitle" data-tour="load-containers">
        Containers to load at {branch ?? '…'}
      </h2>
      {containers.error ? (
        <p role="alert" className="ops-error">
          {containers.error}
        </p>
      ) : null}
      {loadable.length === 0 ? (
        <EmptyState
          title={containers.loading ? 'Loading…' : 'No container is open for loading'}
          description="A container is planned in the office first."
        />
      ) : (
        <ul className="ops-list" aria-label="Containers">
          {loadable.map((c) => (
            <li key={c.id} className="ops-row">
              <div className="ops-row__top">
                <strong>{containerLabel(c)}</strong>
                <Badge tone={containerStatusTone(c.status)}>{containerStatusLabel(c.status)}</Badge>
              </div>
              <div className="ops-muted">
                {c.counts.loaded} loaded ·{' '}
                <TimeInState
                  since={c.stateSince}
                  clock={dueClock(c.nextDueAt, now)}
                  suffix={containerStatusLabel(c.status).toLowerCase()}
                />
              </div>
              <Button size="touch" variant="primary" block onClick={() => onPick(c)}>
                Scan into this container
              </Button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
